import { Button, Card, InfoTip, RotateCcw, cn } from "@app/ui";
import { useEffect, useRef, type ReactNode } from "react";
import { DemoDisclosure } from "../../features/demos/DemoDisclosure.js";
import type { DemoScriptId } from "../../features/demos/registry.js";
import { useI18n } from "../../lib/useI18n.js";
import { useSettingsFocusStore } from "./useSettingsFocus.js";

/** Hook: ref + flash flag for a settings anchor with the given id. */
function useSettingFlash(id: string) {
  const focusId = useSettingsFocusStore((s) => s.focusId);
  const ref = useRef<HTMLDivElement | null>(null);
  const flash = focusId === id;
  useEffect(() => {
    if (!flash || !ref.current) return;
    const el = ref.current;
    const t = setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
    return () => clearTimeout(t);
  }, [flash]);
  return { ref, flash };
}

/**
 * Wraps an existing self-contained card (one that already renders its own
 * `Card`, e.g. AccountCard) so it becomes a search anchor:
 * scrolls into view and shows a fading ring when focused.
 */
export function SettingsAnchor({ id, children }: { id: string; children: ReactNode }) {
  const { ref, flash } = useSettingFlash(id);
  return (
    <div ref={ref} id={`setting-${id}`} className={cn("scroll-mt-4 rounded-lg", flash && "flash-highlight")}>
      {children}
    </div>
  );
}

/**
 * Standard settings card: a titled `Card` with an optional description and an
 * optional "Reset" affordance. Carries an `id` that doubles as the search
 * anchor — when a search result targets it, the card scrolls into view and
 * flashes (mirrors features/calendar-view/useFlashHighlight.ts). The ref lives
 * on an outer wrapper because `Card` does not forward refs.
 */
export function SettingsCard({
  id,
  title,
  description,
  onReset,
  className,
  demoId,
  children,
}: {
  id: string;
  title: string;
  description?: ReactNode;
  onReset?: (() => void) | undefined;
  className?: string;
  /** Ghost-demo script shown behind a "Show me" expander below the controls. */
  demoId?: DemoScriptId;
  children: ReactNode;
}) {
  const { ref, flash } = useSettingFlash(id);
  const { t } = useI18n();

  return (
    <div ref={ref} id={`setting-${id}`} className="scroll-mt-4">
      <Card className={cn("p-4", flash && "flash-highlight", className)}>
        <div className="mb-2 flex items-start justify-between gap-2">
          <h3 className="flex items-center gap-1.5 font-medium">
            {title}
            {description && <InfoTip label={t("common.moreInfo")}>{description}</InfoTip>}
          </h3>
          {onReset && (
            <Button variant="ghost" size="sm" onClick={onReset} aria-label={t("settings.resetAria", { title })}>
              <RotateCcw className="mr-1 h-3.5 w-3.5" /> {t("common.reset")}
            </Button>
          )}
        </div>
        {children}
        {demoId && <DemoDisclosure id={demoId} />}
      </Card>
    </div>
  );
}
